import * as Dialog from '@radix-ui/react-dialog'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Modal — centred overlay for one focused task: confirm, invite, create
// (design/components/modal.html). Built on Radix Dialog (focus trap, Esc,
// scrim, ARIA, return-focus for free). `busy` blocks Esc/scrim close while a
// submit is in flight; `role="alertdialog"` + a confirm-by-typing body is the
// destructive pattern. The tone icon is decorative — the message is text.

const toneBox = {
  info: 'bg-info-bg text-info',
  sens: 'bg-sens-bg text-sens',
} as const

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  icon?: ReactNode
  tone?: 'info' | 'sens'
  size?: 'sm' | 'wide'
  footer?: ReactNode
  children: ReactNode
  role?: 'dialog' | 'alertdialog'
  /** While submitting: Esc/scrim won't close and × is disabled. */
  busy?: boolean
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  tone,
  size = 'sm',
  footer,
  children,
  role = 'dialog',
  busy,
}: ModalProps) {
  const { t } = useTranslation('components')
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o && !busy) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(8,10,14,0.5)]" />
        <Dialog.Content
          role={role}
          onEscapeKeyDown={(e) => {
            if (busy) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (busy || role === 'alertdialog') e.preventDefault()
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'rounded-xl border border-line bg-surface shadow-modal focus:outline-none',
            // Never exceed the viewport: the body scrolls, the header + footer
            // stay pinned so the action buttons are always reachable.
            'flex max-h-[calc(100dvh-2rem)] flex-col',
            size === 'wide' ? 'w-[560px]' : 'w-[440px]',
          )}
        >
          <div className="flex flex-none items-start gap-3 px-5 pb-1 pt-[18px]">
            {icon && (
              <span
                className={cn(
                  'flex size-[34px] flex-none items-center justify-center rounded-md [&_svg]:size-[17px]',
                  tone ? toneBox[tone] : 'bg-field text-text-2',
                )}
                aria-hidden
              >
                {icon}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <Dialog.Title className="block text-[15px] font-bold tracking-[-0.015em] text-text">
                {title}
              </Dialog.Title>
              {subtitle && (
                <Dialog.Description className="mt-[3px] block text-[12px] leading-normal text-text-2">
                  {subtitle}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={t('ui.close')}
                className="flex size-[30px] flex-none items-center justify-center rounded-lg text-text-3 transition-colors hover:bg-hover hover:text-text disabled:opacity-40 [&_svg]:size-[15px]"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2 pt-3 text-[12.5px] leading-relaxed text-text-2">
            {children}
          </div>

          {footer && (
            <div className="flex flex-none items-center gap-[9px] rounded-b-xl border-t border-line bg-surface-2 px-5 py-3.5">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
